/**
 * Non-secret identifier cache. Access + refresh tokens live in HttpOnly
 * cookies issued by the backend; the cockpit cannot (and must not) read
 * them. The cockpit needs companyId + userId in JS to render
 * tenant-/user-scoped UI before the first authenticated API call
 * resolves — both are non-secret and safe in localStorage.
 */

const COMPANY_KEY = "veltric.companyId";
const USER_KEY = "veltric.userId";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  try { return decodeURIComponent(match.slice(name.length + 1)); } catch { return null; }
}

export const tokenStore = {
  getCompanyId(): string | null {
    if (typeof localStorage === "undefined") return null;
    const fromLs = localStorage.getItem(COMPANY_KEY);
    if (fromLs) return fromLs;
    // Fallback for e2e seeders that hand the cockpit a companyId via
    // a non-HttpOnly cookie (addCookies runs before page.goto so
    // localStorage can't be primed). Promote it on first read.
    const fromCookie = readCookie("companyId");
    if (fromCookie) {
      try { localStorage.setItem(COMPANY_KEY, fromCookie); } catch { /* SSR / private mode */ }
      return fromCookie;
    }
    return null;
  },
  setCompanyId(id: string | null) {
    if (typeof localStorage === "undefined") return;
    if (id) localStorage.setItem(COMPANY_KEY, id);
    else localStorage.removeItem(COMPANY_KEY);
  },

  getUserId(): string | null {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(USER_KEY);
  },
  setUserId(id: string | null) {
    if (typeof localStorage === "undefined") return;
    if (id) localStorage.setItem(USER_KEY, id);
    else localStorage.removeItem(USER_KEY);
  },

  clear() {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(USER_KEY);
  },
};
